import type { EnemyDef, EnemyEffect, EnemyState, EnemyTypeId, WorldRef } from './types';
import { BALANCE } from '../data/balance';
import { ENEMIES, ENEMY_BY_ID, bossIdForLevel, zoneForLevel } from '../data/enemies';
import { DEFAULT_BALANCE, type ResolvedBalance } from './balance';
import { enemyGold, enemyHp, levelDayOf, waveOf } from './economy';
import type { Rng } from './rng';
import { endlessPool, isEndless } from './world';

/**
 * Effets **actifs** d'un ennemi (v0.49.0) : `effects`, ou à défaut l'ancien
 * champ `effect` d'un état construit à la main (tests, interface) ; `[]` sans
 * ennemi ni effet.
 */
export function enemyEffects(
  enemy: { effect?: EnemyEffect | null; effects?: EnemyEffect[] } | null | undefined,
): EnemyEffect[] {
  if (!enemy) return [];
  if (Array.isArray(enemy.effects)) return enemy.effects;
  return enemy.effect ? [enemy.effect] : [];
}

/** Premier effet de genre `kind` porté par l'ennemi, ou `null`. */
export function findEffect<K extends EnemyEffect['kind']>(
  enemy: { effect?: EnemyEffect | null; effects?: EnemyEffect[] } | null | undefined,
  kind: K,
): Extract<EnemyEffect, { kind: K }> | null {
  for (const e of enemyEffects(enemy)) if (e.kind === kind) return e as Extract<EnemyEffect, { kind: K }>;
  return null;
}

/** Compteur d'instances, pour donner un identifiant unique à chaque monstre. */
let instanceCounter = 0;

/** Remet le compteur d'instances à zéro (tests déterministes). */
export function resetEnemyCounter(): void {
  instanceCounter = 0;
}

/**
 * Ennemis tirables à la vague W — **A25** : le pool est celui de la **zone du
 * niveau** (zone 10 au-delà du niveau 10), restreint aux ennemis dont le jour
 * minimal d'apparition est atteint. Le jour 6 (Invasion) rend toute la zone
 * tirable. Les boss sont toujours exclus : ils ne sortent que par `makeBoss`.
 *
 * Les poids d'apparition ont disparu en v0.8.0 : le tirage est à **poids
 * égaux** (§F, la table du GD n'en donne aucun).
 *
 * **Mode infini** (phase 5, A82) : au-delà du niveau 10 et avec un monde
 * (`world`), le pool est celui du monde tiré (`endlessPool` : boss **exclus**
 * depuis la v0.49.0, COMMENTS v0.48.1 §1.7). Sans `world`, ou en campagne,
 * rien ne change.
 */
export function spawnPool(wave: number, world?: WorldRef | null): EnemyDef[] {
  const { level, day } = levelDayOf(wave);
  if (world && isEndless(level)) return endlessPool(world, day);
  return zoneForLevel(level).enemies.filter((e) => !e.boss && e.minDay <= day);
}

/** Options d'instanciation d'un ennemi. */
export interface MakeEnemyOptions {
  /**
   * Boss tiré **comme monstre ordinaire** (mode infini, A78) : `isBoss` faux,
   * sa mort ne termine pas l'Invasion. PV et or suivent la mise à l'échelle
   * normale de sa base (48 PV / 50 or).
   */
  asOrdinary?: boolean;
}

/** Instancie un ennemi d'un type donné, mis à l'échelle de la vague W. */
export function makeEnemy(
  def: EnemyDef,
  wave: number,
  bal: ResolvedBalance = DEFAULT_BALANCE,
  opts: MakeEnemyOptions = {},
): EnemyState {
  const maxHp = enemyHp(def.baseHp, wave, bal);
  instanceCounter += 1;
  // v0.49.0 : **copie** des effets de la définition, tous actifs à la fois.
  const effects: EnemyEffect[] = (def.effects ?? []).map((e) => ({ ...e }));
  const revive = effects.find((e): e is Extract<EnemyEffect, { kind: 'revive' }> => e.kind === 'revive');
  return {
    id: `e${instanceCounter}`,
    typeId: def.id,
    name: def.name,
    hp: maxHp,
    maxHp,
    gold: enemyGold(def.baseGold, wave, bal),
    effects,
    /**
     * @deprecated v0.49.0 — lecture du premier effet pour l'affichage et la
     * télémétrie existants ; l'écrire remplace la liste (debug, tests).
     */
    get effect(): EnemyEffect | null {
      return this.effects[0] ?? null;
    },
    set effect(value: EnemyEffect | null) {
      this.effects = value ? [value] : [];
    },
    // A26 : le bouclier est **permanent**, il suit l'effet et ne se brise plus.
    shield: effects.some((e) => e.kind === 'shield'),
    canRevive: revive !== undefined,
    revived: false,
    revivePercent: revive ? revive.percent : 0,
    hits: 0,
    isBoss: def.boss === true && opts.asOrdinary !== true,
    poison: [],
    mutilated: 0,
    // v0.47.0 — *Osseux* et *Du Vide 3* (A94-14, A95-2).
    broken: 0,
    effectVoided: false,
    // v0.49.0 — marque de *Null* (A98-3) et armure des boss.
    nullMark: null,
    armorStacks: 0,
  };
}

/**
 * *Du Vide 3* (v0.47.0, A95-2) : l'ennemi n'a **plus aucun effet** — esquive,
 * bouclier, régénération, résurrection, limite de coups du Serpent, blocage
 * d'emplacements, division du critique, dés Basiques divisés, sorts annulés,
 * vol de temps, assommement, ralentissement, Ingénieur, Poisson Lune : tout
 * lit `effects`, **vidée entière** (v0.49.0 : tous les effets d'un boss). Le
 * bouclier et la résurrection, recopiés à l'instanciation, sont retirés aussi.
 * Le boss y compris. Idempotent ; rend `true` si l'ennemi portait un effet (ou
 * une résurrection) neutralisé.
 */
export function voidEnemyEffect(enemy: EnemyState): boolean {
  if (enemy.effectVoided) return false;
  const had = enemyEffects(enemy).length > 0 || enemy.shield || enemy.canRevive;
  if (!had) return false;
  enemy.effects = [];
  enemy.effect = null;
  enemy.shield = false;
  enemy.canRevive = false;
  enemy.revivePercent = 0;
  enemy.effectVoided = true;
  return true;
}

/**
 * Tire un ennemi dans le pool de la vague W, à **poids égaux** (§F).
 * Le niveau et le jour sont déduits de la vague (`levelDayOf`). `world` : monde
 * du mode infini (`spawnPool`). Un ennemi tiré est toujours **ordinaire** : un
 * boss sorti du pool infini garde `isBoss` faux.
 */
export function rollEnemy(
  wave: number,
  rng: Rng,
  bal: ResolvedBalance = DEFAULT_BALANCE,
  world?: WorldRef | null,
): EnemyState {
  const pool = spawnPool(wave, world);
  const def = pool.length > 0 ? pool[rng.int(0, pool.length - 1)] : ENEMIES[0];
  return makeEnemy(def, wave, bal, { asOrdinary: true });
}

/**
 * PV d'un type d'ennemi au niveau d'expédition `level`, jour `day` — fonction
 * **pure**, sans instanciation ni Rng. Sert à l'interface (« mur suivant » :
 * PV du boss de la prochaine Invasion, jour `BALANCE.invasionDay` = 6) et aux
 * outils d'équilibrage. Renvoie 0 pour un type inconnu.
 */
export function enemyHpAt(
  level: number,
  day: number,
  typeId: string,
  bal: ResolvedBalance = DEFAULT_BALANCE,
): number {
  const def = ENEMY_BY_ID[typeId as EnemyTypeId];
  if (!def) return 0;
  return enemyHp(def.baseHp, waveOf(level, day), bal);
}

/**
 * Le boss de l'Invasion de la vague W, mis à l'échelle — c'est celui de la
 * **zone du niveau** (§F : un boss par zone, 48 PV / 50 or).
 */
export function makeBoss(wave: number, bal: ResolvedBalance = DEFAULT_BALANCE): EnemyState {
  const { level } = levelDayOf(wave);
  return makeEnemy(ENEMY_BY_ID[bossIdForLevel(level)], wave, bal);
}

/**
 * Un boss **donné** pour l'Invasion de la vague W (mode infini, A78 : boss
 * tiré parmi les dix — `state.bossId`). Un identifiant inconnu retombe sur le
 * boss de la zone du niveau, comme `makeBoss`.
 */
export function makeBossOf(
  typeId: EnemyTypeId,
  wave: number,
  bal: ResolvedBalance = DEFAULT_BALANCE,
): EnemyState {
  const def = ENEMY_BY_ID[typeId];
  if (!def || def.boss !== true) return makeBoss(wave, bal);
  return makeEnemy(def, wave, bal);
}

/**
 * Gobelin Ingénieur (§F) : multiplie les PV (max **et** courants) d'un ennemi
 * qui vient d'apparaître. Appelé par `Game` au spawn, avec
 * `combat.nextEnemyHpMult`, qui est ensuite consommé.
 */
export function scaleEnemyHp(enemy: EnemyState, mult: number): void {
  if (!Number.isFinite(mult) || mult <= 0 || mult === 1) return;
  enemy.maxHp = Math.max(1, Math.round(enemy.maxHp * mult));
  enemy.hp = enemy.maxHp;
}

/**
 * Régénération (Sanglier, Vampire, Requin, Amalgame, Plante) : `dt` secondes de
 * soin, part des **PV max** par seconde, jamais au-dessus des PV max et
 * seulement tant que l'ennemi vit (§F). Renvoie les PV réellement rendus.
 */
export function tickRegen(enemy: EnemyState, dt: number): number {
  const regen = findEffect(enemy, 'regen');
  if (!regen || enemy.hp <= 0 || enemy.hp >= enemy.maxHp) return 0;
  const healed = Math.min(enemy.maxHp - enemy.hp, enemy.maxHp * regen.percentPerSecond * dt);
  enemy.hp += healed;
  return healed;
}

/**
 * Convertit une chance **par seconde** en chance sur un pas de `dt` secondes :
 * `1 − (1 − p)^dt`, ce qui redonne exactement `p` sur une seconde quel que soit
 * le découpage (§F — Corbeau noir, Nounours affamé, assommements).
 */
export function chanceForDt(chancePerSecond: number, dt: number): number {
  if (chancePerSecond <= 0 || dt <= 0) return 0;
  if (chancePerSecond >= 1) return 1;
  return 1 - Math.pow(1 - chancePerSecond, dt);
}

/** L'ennemi est-il sous le seuil d'exécution (20 % PV) ? */
export function isExecutable(enemy: EnemyState | null): boolean {
  if (!enemy || enemy.maxHp <= 0) return false;
  return enemy.hp / enemy.maxHp <= BALANCE.executeThreshold;
}

/**
 * Applique des dégâts bruts à un ennemi.
 *
 * Gère la résurrection **unique** des ennemis `revive` (Slime 15 %, Zombie
 * 20 %, Goule 25 %, Monstruosité 33 % — §F) : la part de PV rendue est propre à
 * l'ennemi (`revivePercent`), l'ancienne constante unique a disparu. Le poison
 * est effacé, comme avant.
 */
export function damageEnemy(
  enemy: EnemyState,
  amount: number,
): { killed: boolean; revived: boolean; dealt: number } {
  const dealt = Math.min(enemy.hp, Math.max(0, amount));
  enemy.hp -= dealt;
  if (enemy.hp > 0) return { killed: false, revived: false, dealt };
  enemy.hp = 0;
  if (enemy.canRevive && !enemy.revived) {
    enemy.revived = true;
    enemy.hp = Math.max(1, Math.round(enemy.maxHp * enemy.revivePercent));
    enemy.poison = [];
    return { killed: false, revived: true, dealt };
  }
  return { killed: true, revived: false, dealt };
}

/**
 * Ajoute une pile de poison (cumulable, une pile par lancer empoisonné).
 * Durée : `BALANCE.poisonDuration` (4 s depuis la v0.2.2).
 */
export function addPoison(enemy: EnemyState, dps: number): void {
  if (dps <= 0) return;
  enemy.poison.push({ dps, remaining: BALANCE.poisonDuration });
}

/**
 * *Mutilant* (COMMENTS v0.6.0 §1.3) : l'ennemi subit `affixMutilatedFactor`
 * (×1,3) de dégâts pendant `affixMutilatedDuration` (3 s). Non cumulable : un
 * nouveau déclenchement **relance** simplement le chrono.
 */
export function applyMutilation(enemy: EnemyState): void {
  enemy.mutilated = BALANCE.affixMutilatedDuration;
}

export function isMutilated(enemy: EnemyState | null): boolean {
  return enemy !== null && enemy.mutilated > 0;
}

/** Multiplicateur de dégâts subis par l'ennemi (1 s'il n'est pas mutilé). */
export function mutilationFactor(enemy: EnemyState | null): number {
  return isMutilated(enemy) ? BALANCE.affixMutilatedFactor : 1;
}

/** Fait s'écouler `dt` secondes de mutilation. */
export function tickMutilation(enemy: EnemyState, dt: number): void {
  if (enemy.mutilated > 0) enemy.mutilated = Math.max(0, enemy.mutilated - dt);
}

/**
 * Fait s'écouler `dt` secondes de poison. Renvoie les dégâts infligés.
 * Les piles expirées sont retirées.
 */
export function tickPoison(enemy: EnemyState, dt: number): number {
  if (enemy.poison.length === 0) return 0;
  let total = 0;
  for (const stack of enemy.poison) {
    const active = Math.min(dt, stack.remaining);
    if (active <= 0) continue;
    total += stack.dps * active;
    stack.remaining -= active;
  }
  enemy.poison = enemy.poison.filter((s) => s.remaining > 0);
  return total;
}
